import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
  ArrowRight,
  FileText,
  Flask,
  Lightning,
  MagicWand,
  PencilLine,
  Plus,
  Sparkle,
} from "@phosphor-icons/react";
import slugify from "@sindresorhus/slugify";
import type { ResumeDto } from "@stylingcv/dto";
import { createResumeSchema } from "@stylingcv/dto";
import { getSampleResumeForLocale, idSchema } from "@stylingcv/schema";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Tooltip,
} from "@stylingcv/ui";
import { generateRandomName } from "@stylingcv/utils";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { LocaleComboboxPopover } from "@/client/components/locale-combobox";
import { RESUMES_KEY } from "@/client/constants/query-keys";
import { useCanCreateResume } from "@/client/hooks/use-resume-limits";
import { queryClient } from "@/client/libs/query-client";
import { useCreateResume, useDeleteResume, useUpdateResume } from "@/client/services/resume";
import { useResumes } from "@/client/services/resume";
import { useImportResume } from "@/client/services/resume/import";
import { useUser } from "@/client/services/user";
import { useDialog } from "@/client/stores/dialog";

const formSchema = createResumeSchema.extend({
  id: idSchema.optional(),
  slug: z.string(),
  language: z.string().default("en-US"),
});

type FormValues = z.infer<typeof formSchema>;

export const ResumeDialog = () => {
  const { isOpen, mode, payload, close } = useDialog<ResumeDto>("resume");
  const { i18n } = useLingui();
  const { user } = useUser();
  const { resumes } = useResumes();
  const navigate = useNavigate();
  const [showCreateForm, setShowCreateForm] = useState(false);

  const isCreate = mode === "create";
  const isUpdate = mode === "update";
  const isDelete = mode === "delete";
  const isDuplicate = mode === "duplicate";

  // Check resume limits for creation and duplication
  const currentResumeCount = resumes?.length || 0;
  const { canCreate, upgradeMessage } = useCanCreateResume(currentResumeCount);

  const { createResume, loading: createLoading } = useCreateResume();
  const { updateResume, loading: updateLoading } = useUpdateResume();
  const { deleteResume, loading: deleteLoading } = useDeleteResume();
  const { importResume: duplicateResume, loading: duplicateLoading } = useImportResume();

  const loading = createLoading || updateLoading || deleteLoading || duplicateLoading;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      slug: "",
      language: i18n.locale,
    },
  });

  useEffect(() => {
    if (isOpen) {
      onReset();
      setShowCreateForm(false); // Reset to choice view when dialog opens
    }
  }, [isOpen, payload]);

  useEffect(() => {
    const slug = slugify(form.watch("title"));
    form.setValue("slug", slug);
  }, [form.watch("title")]);

  const onSubmit = async (values: FormValues) => {
    if (isCreate) {
      await createResume({
        title: values.title,
        slug: values.slug,
        language: values.language,
        visibility: "private",
      });
    }

    if (isUpdate) {
      await updateResume({ ...values, id: values.id! });
    }

    if (isDuplicate) {
      await duplicateResume({
        title: values.title,
        slug: values.slug,
        data: payload.item!.data,
      });
    }

    if (isDelete) {
      await deleteResume(values.id!);
    }

    // Invalidation already happens in each mutation's onSuccess callback.
    // Await a single refetch here to ensure the list is updated before closing.
    await queryClient.refetchQueries({ queryKey: RESUMES_KEY });

    close();
  };

  const onReset = () => {
    if (isCreate) form.reset({ title: "", slug: "", language: i18n.locale });
    if (isUpdate)
      form.reset({ id: payload.item?.id, title: payload.item?.title, slug: payload.item?.slug });
    if (isDuplicate)
      form.reset({ title: `${payload.item?.title} (Copy)`, slug: `${payload.item?.slug}-copy` });
    if (isDelete)
      form.reset({ id: payload.item?.id, title: payload.item?.title, slug: payload.item?.slug });
  };

  const onGenerateRandomName = () => {
    const name = generateRandomName();
    form.setValue("title", name);
    form.setValue("slug", slugify(name));
  };

  const onCreateSample = async () => {
    // Check limits before creating sample
    if (!canCreate) {
      close();
      return;
    }

    const randomName = generateRandomName();
    const language = form.getValues("language");

    // Get the localized sample resume based on the selected language
    const localizedSample = getSampleResumeForLocale(language);

    // If user is logged in and has an email, inject it into the sample resume
    if (user?.email) {
      localizedSample.basics.email = user.email;
    }

    await duplicateResume({
      title: randomName,
      slug: slugify(randomName),
      data: localizedSample,
      // language: language,
    });

    await queryClient.refetchQueries({ queryKey: RESUMES_KEY });
    close();
  };

  const handleAIGenerator = () => {
    close();
    navigate("/dashboard/smart-generator");
  };

  const handleCreateEmpty = () => {
    setShowCreateForm(true);
  };

  if (isDelete) {
    return (
      <AlertDialog open={isOpen} onOpenChange={close}>
        <AlertDialogContent>
          <Form {...form}>
            <form>
              <AlertDialogHeader>
                <AlertDialogTitle>{t`Are you sure you want to delete your resume?`}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t`This action cannot be undone. This will permanently delete your resume and cannot be recovered.`}
                </AlertDialogDescription>
              </AlertDialogHeader>

              <AlertDialogFooter>
                <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
                <AlertDialogAction variant="error" onClick={form.handleSubmit(onSubmit)}>
                  {t`Delete`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </form>
          </Form>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="sm:max-w-[540px]">
        {isCreate && !showCreateForm ? (
          // Modern Choice View for Create Mode
          <div className="space-y-5">
            <DialogHeader className="space-y-1 text-center">
              <DialogTitle className="text-xl font-semibold text-gray-900 dark:text-white">
                {t`Create a new resume`}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500 dark:text-gray-400">
                {t`Choose how you want to get started`}
              </DialogDescription>
            </DialogHeader>

            {/* Limit warning */}
            {!canCreate && (
              <div className="rounded-xl border border-red-200/50 bg-red-50/50 p-3 dark:border-red-900/30 dark:bg-red-950/20">
                <p className="text-sm font-medium text-red-700 dark:text-red-300">{t`Resume limit reached`}</p>
                <p className="mt-0.5 text-xs text-red-600/80 dark:text-red-400/70">{upgradeMessage}</p>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              {/* AI Resume Generator Option */}
              <div
                onClick={canCreate ? handleAIGenerator : undefined}
                className={`group relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-violet-200/60 bg-gradient-to-br from-violet-50 via-purple-50/50 to-indigo-50/30 p-4 transition-all duration-300 hover:border-violet-300 hover:shadow-lg hover:shadow-violet-100/50 dark:border-violet-800/30 dark:from-violet-950/30 dark:via-purple-950/20 dark:to-indigo-950/10 dark:hover:border-violet-700/50 dark:hover:shadow-violet-900/20 ${!canCreate ? "pointer-events-none opacity-50" : ""}`}
              >
                {/* Decorative sparkles */}
                <Sparkle 
                  size={10} 
                  weight="fill" 
                  className="absolute right-3 top-3 text-violet-400/50 transition-all duration-500 group-hover:scale-125 group-hover:text-violet-400" 
                />
                <Sparkle 
                  size={6} 
                  weight="fill" 
                  className="absolute right-7 top-6 text-purple-300/40 transition-all duration-700 group-hover:scale-150" 
                />

                {/* Icon */}
                <div className="mb-3 flex size-11 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg shadow-violet-200/50 transition-transform duration-300 group-hover:scale-105 dark:shadow-violet-900/30">
                  <Lightning size={22} weight="fill" className="text-white" />
                </div>

                {/* Content */}
                <h3 className="mb-0.5 text-base font-semibold text-gray-900 dark:text-white">
                  {t`AI Generator`}
                </h3>
                <p className="mb-3 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                  {t`Create tailored resume optimized for your target job`}
                </p>

                {/* Features */}
                <div className="mt-auto flex flex-wrap gap-1.5">
                  <span className="inline-flex items-center rounded-full bg-violet-100/70 px-2 py-0.5 text-[10px] font-medium text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                    {t`ATS Optimized`}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-purple-100/70 px-2 py-0.5 text-[10px] font-medium text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                    {t`Smart`}
                  </span>
                </div>

                {/* Hover indicator */}
                <div className="absolute bottom-3 right-3 flex size-6 items-center justify-center rounded-full bg-violet-100/80 opacity-0 transition-all duration-300 group-hover:opacity-100 dark:bg-violet-800/50">
                  <ArrowRight size={12} className="text-violet-600 dark:text-violet-300" />
                </div>
              </div>

              {/* Create Empty Resume Option */}
              <div
                onClick={canCreate ? handleCreateEmpty : undefined}
                className={`group relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-gray-200/60 bg-gradient-to-br from-gray-50 via-slate-50/50 to-zinc-50/30 p-4 transition-all duration-300 hover:border-gray-300 hover:shadow-lg hover:shadow-gray-100/50 dark:border-gray-700/30 dark:from-gray-900/30 dark:via-slate-900/20 dark:to-zinc-900/10 dark:hover:border-gray-600/50 dark:hover:shadow-gray-800/20 ${!canCreate ? "pointer-events-none opacity-50" : ""}`}
              >
                {/* Icon */}
                <div className="mb-3 flex size-11 items-center justify-center rounded-xl bg-gradient-to-br from-gray-500 to-slate-600 shadow-lg shadow-gray-200/50 transition-transform duration-300 group-hover:scale-105 dark:shadow-gray-800/30">
                  <PencilLine size={22} weight="fill" className="text-white" />
                </div>

                {/* Content */}
                <h3 className="mb-0.5 text-base font-semibold text-gray-900 dark:text-white">
                  {t`Start from Scratch`}
                </h3>
                <p className="mb-3 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                  {t`Build manually with full creative control`}
                </p>

                {/* Features */}
                <div className="mt-auto flex flex-wrap gap-1.5">
                  <span className="inline-flex items-center rounded-full bg-gray-100/70 px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-800/60 dark:text-gray-300">
                    {t`Custom`}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-slate-100/70 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-800/60 dark:text-slate-300">
                    {t`Flexible`}
                  </span>
                </div>

                {/* Hover indicator */}
                <div className="absolute bottom-3 right-3 flex size-6 items-center justify-center rounded-full bg-gray-100/80 opacity-0 transition-all duration-300 group-hover:opacity-100 dark:bg-gray-700/50">
                  <ArrowRight size={12} className="text-gray-600 dark:text-gray-300" />
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Modern Form View for Update/Duplicate or when showCreateForm is true
          <Form {...form}>
            <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
              <DialogHeader className="space-y-1">
                <DialogTitle className="text-xl font-semibold text-gray-900 dark:text-white">
                  {isCreate && t`Name your resume`}
                  {isUpdate && t`Rename resume`}
                  {isDuplicate && t`Duplicate resume`}
                </DialogTitle>
                <DialogDescription className="text-sm text-gray-500 dark:text-gray-400">
                  {isCreate && t`Give your resume a memorable name`}
                  {isUpdate && t`Update the name of your resume`}
                  {isDuplicate && t`Create a copy with a new name`}
                </DialogDescription>
              </DialogHeader>

              {/* Show limit warning for create/duplicate */}
              {(isCreate || isDuplicate) && !canCreate && (
                <div className="rounded-xl border border-red-200/50 bg-red-50/50 p-3 dark:border-red-900/30 dark:bg-red-950/20">
                  <p className="text-sm font-medium text-red-700 dark:text-red-300">{t`Resume limit reached`}</p>
                  <p className="mt-0.5 text-xs text-red-600/80 dark:text-red-400/70">{upgradeMessage}</p>
                </div>
              )}

              <div className="space-y-4">
                <FormField
                  name="title"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">{t`Resume Name`}</FormLabel>
                      <FormControl>
                        <div className="flex items-center gap-2">
                          <Input 
                            {...field} 
                            placeholder={t`e.g., Software Engineer at Google`}
                            className="h-11 rounded-xl border-gray-200 bg-gray-50/50 transition-colors focus:border-violet-300 focus:bg-white focus:ring-violet-200 dark:border-gray-700 dark:bg-gray-800/50 dark:focus:border-violet-600 dark:focus:bg-gray-800" 
                          />
                          {(isCreate || isDuplicate) && (
                            <Tooltip content={t`Generate random name`}>
                              <Button
                                size="icon"
                                type="button"
                                variant="outline"
                                onClick={onGenerateRandomName}
                                className="size-11 shrink-0 rounded-xl border-gray-200 hover:border-violet-300 hover:bg-violet-50 dark:border-gray-700 dark:hover:border-violet-600 dark:hover:bg-violet-950/30"
                              >
                                <MagicWand size={18} className="text-gray-500" />
                              </Button>
                            </Tooltip>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {isCreate && (
                  <FormField
                    name="language"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">{t`Language`}</FormLabel>
                        <FormControl>
                          <LocaleComboboxPopover value={field.value} onValueChange={field.onChange} />
                        </FormControl>
                        <FormDescription className="text-xs text-gray-500 dark:text-gray-400">
                          {t`Section names will be in this language`}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <DialogFooter className="flex-col gap-2 sm:flex-row">
                {isCreate && showCreateForm && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowCreateForm(false)}
                    className="order-3 h-10 rounded-xl text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200 sm:order-1"
                  >
                    {t`Back`}
                  </Button>
                )}

                <div className="flex flex-1 justify-end gap-2">
                  {isCreate && showCreateForm && (
                    <Tooltip content={t`Create a pre-filled sample resume to explore the editor`}>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={!canCreate}
                        onClick={onCreateSample}
                        className="h-10 rounded-xl border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-800"
                      >
                        <Flask size={16} className="me-1.5" />
                        {t`Sample`}
                      </Button>
                    </Tooltip>
                  )}

                  <Tooltip content={isCreate ? t`Create a blank resume from scratch` : undefined}>
                    <Button
                      type="submit"
                      disabled={loading || ((isCreate || isDuplicate) && !canCreate)}
                      className="h-10 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 px-5 font-medium text-white shadow-lg shadow-violet-200/50 transition-all hover:from-violet-700 hover:to-purple-700 hover:shadow-violet-300/50 dark:shadow-violet-900/30 dark:hover:shadow-violet-800/40"
                    >
                      {isCreate && t`Create Resume`}
                      {isUpdate && t`Save Changes`}
                      {isDuplicate && t`Duplicate`}
                    </Button>
                  </Tooltip>
                </div>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
